// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { LMModel } from '@form/model';
import { AppConstants } from '@utils/app.constants';
import { LMContextService } from '@services/lm-context.service';

export enum LMLayoutType {
  PROGRESS = 0,
  BINARY_DOCUMENT,
  CHECKBOX,
  CHECKCONTROL,
  PASSWORD,
  DATE,
  TIME,
  RADIO,
  DROPDOWN,
  ONE_LINE_TEXTAREA,
  MULTI_LINE_TEXTAREA,
  BUSCLASS_FIELD,
  RICH_TEXT,
  LOOKUP,
  TEXT,
  MULTI_SELECT,
  IMAGE,
  MANUAL_REPRESENTATION,
  COMPACT_FORMAT,
  COMPACT_FORMAT_BY_FORM,
  FIELD_EDITOR,
  FIELD_EDITOR_COMPARE,
  RATING,
  PROCESS_INDICATOR,
  CAPTCHA_CONTROL,
  CHECKBOXGROUP,
}

@Injectable()
export class LMFieldLayoutService {

  constructor(
    protected lmContextService: LMContextService
  ) {}

  // public methods
  public getLayoutType(field: LMFormLayoutField, specField: LMSpecField, model: LMModel, paragraphLayoutInfo: LMParagraphLayoutInfo): LMLayoutType {
    if (field.type === 'progress') {
      return LMLayoutType.PROGRESS;

    } else if (field.displayAsRating) {
      return LMLayoutType.RATING;

    } else if (field.type === 'binarydocument') {
      return LMLayoutType.BINARY_DOCUMENT;

    } else if (field.type === 'checkbox' && !model.isSearchForm) {
      return LMLayoutType.CHECKBOX;

    } else if (field.type === 'checkbox' && model.isSearchForm) {
      return LMLayoutType.DROPDOWN;

    } else if (field.type === 'CHECKCONTROL') {
      return LMLayoutType.CHECKCONTROL;

    } else if (field.type === 'password') {
      return LMLayoutType.PASSWORD;

    } else if (this.isDate(field, specField)) {
      if (specField.type === AppConstants.TIMESTAMP) {
        if (this.isDisplayDateOnly(field, specField)) {
          return LMLayoutType.DATE;

        } else if (this.isDisplayHoursAndMinutesOnly(field, specField) || this.isDisplayTimeOnly(field, specField)) {
          return LMLayoutType.TIME;
        }
      }
      return LMLayoutType.DATE;

    } else if (this.isTime(field, specField)) {
      return LMLayoutType.TIME;

    } else if (field.type === 'state' && this.isRadio(field, specField, model, paragraphLayoutInfo) && !this.isCheckbox(field, specField, model, paragraphLayoutInfo)) {
      return LMLayoutType.RADIO;

    } else if (field.type === 'state' && !this.isRadio(field, specField, model, paragraphLayoutInfo) && !this.isCheckbox(field, specField, model, paragraphLayoutInfo)) {
      return LMLayoutType.DROPDOWN;

    } else if (field.type === 'state' && this.isCheckbox(field, specField, model, paragraphLayoutInfo) && !this.isRadio(field, specField, model, paragraphLayoutInfo)) {
      return LMLayoutType.CHECKBOXGROUP;

    } else if (this.hasOneLineTextArea(field) && !model.isSearchForm && !this.isProcessIndicator(field)) {
      return LMLayoutType.ONE_LINE_TEXTAREA;

    } else if (!model.isSearchForm && this.isFieldEditorComparer(field, specField)) {
      return LMLayoutType.FIELD_EDITOR_COMPARE;

    } else if (!model.isSearchForm && this.isFieldEditor(specField)) {
      return LMLayoutType.FIELD_EDITOR;

    } else if (this.hasMultiLineTextArea(field) && !model.isSearchForm && field.busClassField) {
      return LMLayoutType.BUSCLASS_FIELD;

    } else if (this.hasMultiLineTextArea(field) && !model.isSearchForm && !this.isProcessIndicator(field)) {
      return LMLayoutType.MULTI_LINE_TEXTAREA;

    } else if (this.isProcessIndicator(field) && !model.isSearchForm) {
      return LMLayoutType.PROCESS_INDICATOR;

    } else if (field.type === 'richText') {
      return LMLayoutType.RICH_TEXT;

    } else if (!this.isDate(field, specField) && !this.isTime(field, specField)
      && ((field.type === 'text' || field.type === 'compactFormat') && this.isSelectable(field)
      || (field.type === 'text' || field.type === 'compactFormat' || field.type === 'textarea') && this.isScannable(field))) {
      return LMLayoutType.LOOKUP;

    } else if (field.type === 'multiselect') {
      return LMLayoutType.MULTI_SELECT;

    } else if (field.type === 'image' || (field.type === 'url' && field.displayImageAs)) {
      return LMLayoutType.IMAGE;

      // } else if (field.type === 'compactFormat' && !this.isSelectable(field)) {
      //   return LMLayoutType.COMPACT_FORMAT;
      //
      // } else if (field.type === 'compactFormatByForm') {
      //   return LMLayoutType.COMPACT_FORMAT_BY_FORM;

    } else if ((field.type === 'compactFormat' && !this.isSelectable(field))
      || field.type === 'compactFormatByForm') {
      if (model.isNestedCompoundField(specField)) {
        return LMLayoutType.COMPACT_FORMAT;
      }

      return LMLayoutType.COMPACT_FORMAT_BY_FORM;

    } else {
      return LMLayoutType.TEXT;
    }
  }

  public hasLimitToMinuteIncrements(field: LMFormLayoutField, specField: LMSpecField): boolean {
    return ((specField?.type === AppConstants.TIMESTAMP || specField?.type === AppConstants.TIME) && field.hasOwnProperty('limitToMinuteIncrements'));
  }

  public hasTimeZone(field: LMFormLayoutField, specField: LMSpecField): boolean {
    return (specField?.type === AppConstants.TIMESTAMP && field.hasOwnProperty('timeZoneIs'));
  }

  public hasDisableDST(field: LMFormLayoutField, specField: LMSpecField): boolean {
    return (specField?.type === AppConstants.TIMESTAMP && field.hasOwnProperty('disableDST'));
  }

  public isDisplayWithTimeZone(field: LMFormLayoutField, specField: LMSpecField): boolean {
    return (specField?.type === AppConstants.TIMESTAMP && field.hasOwnProperty('displayWithTimeZone'));
  }

  public isDisplayDateOnly(field: LMFormLayoutField, specField: LMSpecField): boolean {
    return (specField.type === AppConstants.TIMESTAMP && field.hasOwnProperty('displayDateOnly'));
  }

  public isDisplayTimeOnly(field: LMFormLayoutField, specField: LMSpecField): boolean {
    return (specField.type === AppConstants.TIMESTAMP && field.hasOwnProperty('displayTimeOnly'));
  }

  public isDisplayHoursAndMinutesOnly(field: LMFormLayoutField, specField: LMSpecField): boolean {
    return ((specField.type === AppConstants.TIMESTAMP || specField.type === AppConstants.TIME) && field.hasOwnProperty('displayHoursAndMinutesOnly'));
  }

  public isDisplayDateAndHoursAndMinutes(field: LMFormLayoutField, specField: LMSpecField): boolean {
    return ((specField.type === AppConstants.TIMESTAMP) && field.hasOwnProperty('displayDateAndHoursAndMinutes'));
  }

  public specFieldToLayoutType(field: LMFormLayoutField, specField: LMSpecField): LMLayoutType {
    if (specField.isMultiselect) {
      return LMLayoutType.MULTI_SELECT;
    }

    if ((specField.stateValues && specField.stateValues.length > 0) || specField.hasUserDefinedStates || field.displayAsDropdown) {
      return LMLayoutType.DROPDOWN;
    }

    if (specField.type === 'TEXT') {
      return LMLayoutType.TEXT;
    }

    return LMLayoutType.TEXT;
  }

  // private methods
  private isDate(field: LMFormLayoutField, specField: LMSpecField): boolean {
    if (!(!!specField)) {
      return false;
    }
    return (field.type === 'date'
      || (field.type === 'text'
        && (specField?.type === AppConstants.MMDD
          || specField?.type === AppConstants.TIMESTAMP
          || specField?.type === AppConstants.YYYY
          || specField?.type === AppConstants.YYYYMM
          || specField?.type === AppConstants.YYYYMMDD)));
  }

  private isTime(field: LMFormLayoutField, specField: LMSpecField): boolean {
    if (!(!!specField)) {
      return false;
    }
    return (field.type === 'text' && specField?.type === AppConstants.TIME);
  }

  private isRadio(field: LMRadioButtonLayout, specField: LMSpecField, model: LMModel, paragraphLayoutInfo: LMParagraphLayoutInfo): boolean {
    if (!(!!specField)) {
      return false;
    }

    const isRadio = !!(field.type === 'state' && field.displayAsRadioButtons);

    if (isRadio && ((paragraphLayoutInfo && paragraphLayoutInfo.isCompoundField)
      || this.isCompactForm(model.formSpec))) {
      // don't allow radio buttons in lm-compound-field or compact form
      return false;
    }
    return isRadio;
  }

  private isCheckbox(field: LMCheckboxButtonLayout, specField: LMSpecField, model: LMModel, paragraphLayoutInfo: LMParagraphLayoutInfo): boolean {
    if (!(!!specField)) {
      return false;
    }

    const isCheckbox = !!(model.isSearchForm && field.type === 'state' && (field.displayAsCheckboxes || field.horizontalCheckboxes));

    if (isCheckbox && ((paragraphLayoutInfo && paragraphLayoutInfo.isCompoundField)
        || this.isCompactForm(model.formSpec))) {
      // don't allow checkbox buttons in lm-compound-field or compact form
      return false;
    }
    return isCheckbox;
  }

  private isBlob(field: LMFormLayoutField): boolean {
    return (field.type === 'binaryobject' || field.type === 'binarydocument');
  }

  private isSelectable(field: LMLookupLayout): boolean {
    return field.selectable || false;
  }

  private isScannable(field: LMLookupLayout): boolean {
    if (this.lmContextService.inNativeMobileApp()) {
      return field.scannable;
    }
    return false;
  }

  private hasOneLineTextArea(field: LMFormLayoutField): boolean {
    let lines: number;
    if (field.type === 'textarea') {
      lines = (field.lines === undefined || field.lines === 0) ? 4 : field.lines;
      return lines === 1;
    } else {
      return false;
    }
  }

  private hasMultiLineTextArea(field: LMFormLayoutField): boolean {
    let lines: number;
    if (field.type === 'textarea') {
      lines = (field.lines === undefined || field.lines === 0) ? 4 : field.lines;
      return lines > 1;
    } else {
      return false;
    }
  }

  private isFieldEditor(specField: LMSpecField): boolean {
    if (!this.lmContextService.getContext()) { // being extra careful!
      return false;
    }

    if (specField?.isLpl) {
      return this.lmContextService.hasConfigAccess() || this.lmContextService.getContext().webApp === 'ConfigurationConsole' || this.lmContextService.getContext().webApp == 'SecurityAdministration';
    }

    return false;
  }

  private isFieldEditorComparer(field: LMFormLayoutField, specField: LMSpecField): boolean {
    //
    // if (!this.lmContextService.getContext()) { // being extra careful!
    //   return false;
    // }
    // if (this.lmContextService.getContext().webApp !== 'ConfigurationConsole' && this.lmContextService.getContext().webApp !== 'SecurityAdministration') {
    //   return false;
    // }

    return !(!field.compareWith);
  }

  private isCompactForm(formSpec: LMFormSpec): boolean {
    return (formSpec && formSpec.busFormSpec.subFormType
      && formSpec.busFormSpec.subFormType === AppConstants.FORMSUBTYPE_COMPOUNDFLD
    );
  }

  private isProcessIndicator(field: LMFormLayoutField) {
    return field.isProcessIndicator && field.type === 'textarea' && (field.displayOnly || field.displayAsText);
  }
}
